import { useCallback, useMemo, useState } from 'react';

/**
 * Collapse state for the conversation-history panel: the user's standing
 * preference, kept in localStorage (same single-boolean pattern as
 * `lib/crm/record/hooks/useRailCollapsed.ts`).
 *
 * Deliberately nothing else drives this — an earlier version auto-collapsed
 * the panel while a proposal pane was open, and it was removed: the panel
 * collapses when the user collapses it, and stays wherever they put it.
 */

const COLLAPSED_KEY = 'conversation-history-panel-collapsed';

function readPersistedCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    // Private mode / blocked storage — fall back to expanded.
    return false;
  }
}

function persistCollapsed(value: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, String(value));
  } catch {
    // Best-effort: the in-session state still works without persistence.
  }
}

export interface ConversationHistoryCollapseState {
  collapsed: boolean;
  toggleCollapsed: () => void;
}

export function useConversationHistoryCollapsed(): ConversationHistoryCollapseState {
  const [collapsed, setCollapsed] = useState(readPersistedCollapsed);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(v => {
      const next = !v;
      persistCollapsed(next);
      return next;
    });
  }, []);

  return useMemo(
    () => ({ collapsed, toggleCollapsed }),
    [collapsed, toggleCollapsed]
  );
}
